import { useLayoutEffect, useRef, useState } from 'react';
import useElementWidth from './useElementWidth';

/**
 * Compact level (0 = full size, up to `maxLevel`) at which the children of the
 * flex row in `ref` fit on one line. Measures the real buttons instead of fixed
 * breakpoints, so it holds for any zoom, font or button set. It steps one level
 * per pass before paint and remembers the width each level needed, so it can
 * step back when room returns. `deps` = anything that changes the row's content.
 */
export default function useFitLevel(ref, maxLevel, deps = []) {
  const [level, setLevel] = useState(0);
  const neededAt = useRef([]);
  const width = useElementWidth(ref);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || width === null) return;
    const style = getComputedStyle(el);
    const kids = [...el.children];
    const gap = parseFloat(style.columnGap) || 0;
    const needed = kids.reduce((sum, kid) => sum + kid.offsetWidth, 0) + gap * Math.max(kids.length - 1, 0);
    const room = el.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);
    neededAt.current[level] = needed;
    if (needed > room && level < maxLevel) setLevel(level + 1);
    else if (level > 0 && neededAt.current[level - 1] <= room) setLevel(level - 1);
  }, [width, level, maxLevel, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps
  return level;
}
